/**
 * EditableLinkNode - 可编辑链接节点（Inline 编辑）
 *
 * 特性：
 * - DecoratorNode：渲染为 inline input
 * - 双击链接时替换为此节点
 * - Enter 确认，Esc 取消
 * - 保持在文本流中
 */

import { $createLinkNode } from "@lexical/link";
import {
  $applyNodeReplacement,
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  DecoratorNode,
  type LexicalEditor,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  Spread,
} from "lexical";
import { type KeyboardEvent as ReactKeyboardEvent, useCallback, useLayoutEffect, useRef, useState } from "react";

declare global {
  interface Window {
    editor?: LexicalEditor;
  }
}

export type SerializedEditableLinkNode = Spread<
  {
    type: "editableLink";
    version: 1;
    url: string;
    text: string;
  },
  SerializedLexicalNode
>;

export class EditableLinkNode extends DecoratorNode<JSX.Element> {
  __url: string;
  __text: string;

  static getType(): string {
    return "editableLink";
  }

  static clone(node: EditableLinkNode): EditableLinkNode {
    return new EditableLinkNode(node.__url, node.__text, node.__key);
  }

  constructor(url: string, text: string, key?: NodeKey) {
    super(key);
    this.__url = url;
    this.__text = text;
  }

  createDOM(): HTMLElement {
    const span = document.createElement("span");
    span.style.display = "inline-block";
    return span;
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(): JSX.Element {
    return <EditableLinkEditor url={this.__url} text={this.__text} nodeKey={this.__key} />;
  }

  static importJSON(serializedNode: SerializedEditableLinkNode): EditableLinkNode {
    return new EditableLinkNode(serializedNode.url, serializedNode.text);
  }

  exportJSON(): SerializedEditableLinkNode {
    return {
      ...super.exportJSON(),
      type: "editableLink",
      version: 1,
      url: this.__url,
      text: this.__text,
    };
  }

  getTextContent(): string {
    return this.__text || this.__url;
  }

  isEqualTo(other: LexicalNode): boolean {
    return !(other instanceof EditableLinkNode) || this.__url !== other.__url || this.__text !== other.__text;
  }
}

export function $createEditableLinkNode(url: string, text: string): EditableLinkNode {
  const node = new EditableLinkNode(url, text);
  return $applyNodeReplacement(node);
}

function EditableLinkEditor({ url, text, nodeKey }: { url: string; text: string; nodeKey: string }) {
  const [linkUrl, setLinkUrl] = useState(url);
  const [linkText, setLinkText] = useState(text || url);
  const inputRef = useRef<HTMLInputElement>(null);
  const input2Ref = useRef<HTMLInputElement>(null);

  // 定义保存和取消函数（需要在 useEffect 之前）
  const handleSave = useCallback(() => {
    // Convert to regular LinkNode
    const editor = window.editor; // Global reference to editor
    if (!editor) {
      return;
    }

    editor.update(() => {
      const node = editor.getEditorState()._nodeMap.get(nodeKey);

      if (node instanceof EditableLinkNode) {
        const linkNode = $createLinkNode(linkUrl);
        const textNode = $createTextNode(linkText || linkUrl);
        linkNode.append(textNode);
        node.replace(linkNode);

        // Move selection after the link
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          linkNode.selectEnd();
        }
      }
    });
  }, [linkUrl, linkText, nodeKey]);

  const handleCancel = useCallback(() => {
    // Revert to original link
    const editor = window.editor;
    if (!editor) {
      return;
    }

    editor.update(() => {
      const node = editor.getEditorState()._nodeMap.get(nodeKey);

      if (node instanceof EditableLinkNode) {
        const linkNode = $createLinkNode(url);
        const textNode = $createTextNode(text || url);
        linkNode.append(textNode);
        node.replace(linkNode);
      }
    });
  }, [url, text, nodeKey]);

  // Auto-focus input when mounted
  useLayoutEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const handleEditorKeyDownCapture = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      const key = event.key;
      const isCmdOrCtrl = event.metaKey || event.ctrlKey;
      const nativeEvent = event.nativeEvent as KeyboardEvent;

      if (key === "Escape" || key === "Esc") {
        event.preventDefault();
        event.stopPropagation();
        nativeEvent.stopImmediatePropagation?.();
        handleCancel();
        return;
      }

      if (key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        nativeEvent.stopImmediatePropagation?.();
        handleSave();
        return;
      }

      if (isCmdOrCtrl) {
        event.stopPropagation();
        nativeEvent.stopImmediatePropagation?.();
      }
    },
    [handleCancel, handleSave],
  );

  return (
    <span className="inline-flex items-center gap-2 mx-1 p-2 bg-background border rounded-lg" onKeyDownCapture={handleEditorKeyDownCapture}>
      <input
        ref={inputRef}
        type="text"
        value={linkUrl}
        onChange={(e) => setLinkUrl(e.target.value)}
        className="h-7 w-64 text-sm inline-block border border-primary rounded px-2"
        placeholder="链接 URL"
      />
      <input
        ref={input2Ref}
        type="text"
        value={linkText}
        onChange={(e) => setLinkText(e.target.value)}
        className="h-7 w-40 text-sm inline-block border rounded px-2"
        placeholder="显示文本"
      />
      <div className="flex gap-1">
        <button
          type="button"
          onClick={handleSave}
          className="h-7 px-3 text-sm bg-primary text-primary-foreground hover:bg-primary/90 rounded"
        >
          保存
        </button>
        <button
          type="button"
          onClick={handleCancel}
          className="h-7 px-3 text-sm bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded"
        >
          取消
        </button>
      </div>
    </span>
  );
}
